/**
 * sessionStorage persistence for the currently active duel's challenge id
 * (design.md D1/D6, spec `duel-spa-navigation` — "Active Challenge Persists
 * Via sessionStorage"). Kept as a tiny, defensive wrapper so a missing/
 * disabled `sessionStorage` (privacy mode, SSR, etc.) never throws and
 * degrades to "no persisted active challenge" instead of crashing `/duels`.
 */

export const ACTIVE_CHALLENGE_KEY = "activeChallengeId";

/**
 * Reads the persisted active challenge id, if any. Numeric-looking values
 * are returned as numbers so they compare equal (`===`) to the `id` field
 * produced by `normalizeChallenge`.
 *
 * @returns {number|string|null}
 */
export const getActiveChallengeId = () => {
  try {
    const raw = window.sessionStorage.getItem(ACTIVE_CHALLENGE_KEY);
    if (raw === null || raw === "") return null;
    const numeric = Number(raw);
    return Number.isNaN(numeric) ? raw : numeric;
  } catch (err) {
    return null;
  }
};

/**
 * Persists the active challenge id. Passing `null`/`undefined` clears it
 * (equivalent to `clearActiveChallengeId`).
 *
 * @param {number|string|null|undefined} id
 */
export const setActiveChallengeId = (id) => {
  try {
    if (id === null || id === undefined) {
      window.sessionStorage.removeItem(ACTIVE_CHALLENGE_KEY);
      return;
    }
    window.sessionStorage.setItem(ACTIVE_CHALLENGE_KEY, String(id));
  } catch (err) {
    // sessionStorage may be unavailable — fail silently, feature degrades to
    // "no persisted active challenge" rather than crashing the page.
  }
};

/**
 * Clears the persisted active challenge id.
 */
export const clearActiveChallengeId = () => {
  try {
    window.sessionStorage.removeItem(ACTIVE_CHALLENGE_KEY);
  } catch (err) {
    // ignore
  }
};
